/**
 * pricing.ts — single source of truth for financial fees and calculations.
 *
 * This file is mirrored in supabase/functions/_shared/pricing.ts.
 * Any change here must be replicated there.
 *
 * ─── Decided ───────────────────────────────────────────────────────────────
 *  - Escrow model: sOSSiety captures the payment and transfers after delivery.
 *  - Stripe processing fees are covered by a practitioner-paid gross-up service fee.
 *  - Payout fee ($0.25) is absorbed by the platform.
 *  - Payout cadence is a product rule, not implied by Stripe transfers
 *
 * ─── Pending decisions ────────────────────────────────────────────────────
 *  - PLATFORM_FLAT_FEE_CENTS: legacy; Roll Review uses Stripe fee gross-up.
 *  - SERVICE_TAX: use Stripe Tax, fixed tax, or no tax.
 *    See SERVICE_TAX_MODEL below.
 */

// ─── Fee constants ─────────────────────────────────────────────────────────────

/**
 * Platform commission on the instructor-defined price. Effective May 2026.
 * See SOS-319.
 *
 * Changing this value impacts:
 *   - Checkout breakdown display.
 *   - payments.platform_fee_cents.
 *   - stripe-release-transfer net amount.
 *   - pricing/currency tests.
 */
export const PLATFORM_COMMISSION_PERCENT = 20;

/**
 * Legacy fixed platform fee. Roll Review currently uses Stripe fee gross-up
 * instead of a fixed service fee.
 *
 * If this is revived later, prefer using PricingPolicy.serviceFeeCents with
 * serviceFeeMode = 'fixed' instead of reading this constant directly.
 */
export const PLATFORM_FLAT_FEE_CENTS = 0;

/**
 * Not final: service tax model.
 *
 * 'none'       -> no tax.
 * 'stripe_tax' -> Stripe Tax calculates tax at checkout.
 * 'fixed'      -> fixed tax/service fee model.
 *
 * Stripe Tax does not change sOSSiety commission. It is an extra
 * practitioner-paid amount managed by Stripe.
 */
export const SERVICE_TAX_MODEL: 'none' | 'stripe_tax' | 'fixed' = 'none';

export type FeeModel = 'commission_only' | 'service_fee_plus_commission';

export type FeePayer = 'platform' | 'instructor' | 'practitioner';

export type ServiceFeeMode = 'none' | 'fixed' | 'stripe_gross_up';

export interface PricingPolicy {
  feeModel: FeeModel;
  commissionPercent: number;
  serviceFeeCents: number;
  serviceFeeMode: ServiceFeeMode;
  stripeProcessingFeePaidBy: FeePayer;
  payoutFeePaidBy: FeePayer;
}

export const ROLL_REVIEW_PRICING_POLICY: PricingPolicy = {
  feeModel: 'service_fee_plus_commission',
  commissionPercent: PLATFORM_COMMISSION_PERCENT,
  serviceFeeCents: PLATFORM_FLAT_FEE_CENTS,
  serviceFeeMode: 'stripe_gross_up',
  stripeProcessingFeePaidBy: 'practitioner',
  payoutFeePaidBy: 'platform',
};

// ─── Decided constants ─────────────────────────────────────────────────────────

/**
 * Payout transfer cost. Currently absorbed by sOSSiety.
 * If passed to instructors later, subtract it from the transfer amount.
 */
export const PAYOUT_FEE_CENTS = 25;

/**
 * Stripe processing fees by instructor country.
 * Roll Review grosses up the practitioner total so these fees are covered
 * by the service fee, not by reducing instructor net or sOSSiety commission.
 */
export const STRIPE_PROCESSING_FEES: Record<
  string,
  { percent: number; fixed: number; currency: string }
> = {
  US: { percent: 2.9,  fixed: 0.30, currency: 'USD' },
  BR: { percent: 2.9,  fixed: 0.39, currency: 'BRL' },
  // Add other countries as they become supported.
};

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface PriceBreakdown {
  /** Fee model used to calculate this breakdown. */
  feeModel: FeeModel;

  /** Price defined by the instructor. */
  instructorPriceCents: number;

  /** Service fee charged to the practitioner on top of instructor price. */
  serviceFeeCents: number;

  /** Total charged to the practitioner. */
  practitionerTotalCents: number;

  /** sOSSiety commission on instructorPriceCents. */
  commissionCents: number;

  /** Stripe processing fee calculated on practitionerTotalCents. */
  stripeProcessingFeeCents: number;

  /** Platform gross revenue before Stripe/payout fees. */
  platformGrossRevenueCents: number;

  /** Platform revenue after Stripe fee, before payout fee. */
  platformNetRevenueBeforePayoutCents: number;

  stripeProcessingFeePaidBy: FeePayer;
  payoutFeePaidBy: FeePayer;

  /** Backward-compatible alias: total charged to practitioner. */
  priceCents: number;

  /** Backward-compatible alias: serviceFeeCents. */
  platformFlatFeeCents: number;

  /** Backward-compatible alias: instructorPriceCents. */
  subtotalCents: number;

  /** Backward-compatible alias: commissionCents. */
  platformCommissionCents: number;
  platformCommissionPercent: number;

  /** Backward-compatible alias: stripeProcessingFeeCents. */
  stripeFeesCents: number;

  /** Instructor net amount. */
  netToInstructorCents: number;

  /** Effective instructor percentage over practitioner total. */
  percentToInstructor: number;
}

// ─── Calculation ───────────────────────────────────────────────────────────────

export interface FeeBreakdownInput {
  instructorPriceCents: number;
  countryCode?: string;
  policy?: PricingPolicy;
}

function calculateStripeProcessingFeeCents(totalCents: number, countryCode: string): number {
  const stripeFeeConfig =
    STRIPE_PROCESSING_FEES[countryCode] ?? STRIPE_PROCESSING_FEES.US;

  return Math.round(
    (totalCents * stripeFeeConfig.percent) / 100 + stripeFeeConfig.fixed * 100
  );
}

function calculateGrossedUpTotalCents(instructorPriceCents: number, countryCode: string): number {
  const stripeFeeConfig =
    STRIPE_PROCESSING_FEES[countryCode] ?? STRIPE_PROCESSING_FEES.US;
  const fixedFeeCents = Math.round(stripeFeeConfig.fixed * 100);
  const percentageMultiplier = 1 - stripeFeeConfig.percent / 100;

  return Math.ceil((instructorPriceCents + fixedFeeCents) / percentageMultiplier);
}

export function calculateFeeBreakdown({
  instructorPriceCents,
  countryCode = 'US',
  policy = ROLL_REVIEW_PRICING_POLICY,
}: FeeBreakdownInput): PriceBreakdown {
  const practitionerTotalCents =
    policy.feeModel === 'service_fee_plus_commission' && policy.serviceFeeMode === 'stripe_gross_up'
      ? calculateGrossedUpTotalCents(instructorPriceCents, countryCode)
      : instructorPriceCents + (policy.feeModel === 'service_fee_plus_commission' ? policy.serviceFeeCents : 0);

  const serviceFeeCents = practitionerTotalCents - instructorPriceCents;
  const commissionCents = Math.round(
    (instructorPriceCents * policy.commissionPercent) / 100
  );

  const stripeProcessingFeeCents = calculateStripeProcessingFeeCents(practitionerTotalCents, countryCode);
  const netToInstructorCents = instructorPriceCents - commissionCents;
  const platformGrossRevenueCents = serviceFeeCents + commissionCents;
  const platformNetRevenueBeforePayoutCents =
    platformGrossRevenueCents - stripeProcessingFeeCents;

  const percentToInstructor =
    practitionerTotalCents > 0
      ? Math.round((netToInstructorCents / practitionerTotalCents) * 1000) / 10
      : 0;

  return {
    feeModel: policy.feeModel,
    instructorPriceCents,
    serviceFeeCents,
    practitionerTotalCents,
    commissionCents,
    stripeProcessingFeeCents,
    netToInstructorCents,
    platformGrossRevenueCents,
    platformNetRevenueBeforePayoutCents,
    stripeProcessingFeePaidBy: policy.stripeProcessingFeePaidBy,
    payoutFeePaidBy: policy.payoutFeePaidBy,

    // Backward-compatible aliases used by the current Roll Review UI/Edge code.
    priceCents: practitionerTotalCents,
    platformFlatFeeCents: serviceFeeCents,
    subtotalCents: instructorPriceCents,
    platformCommissionCents: commissionCents,
    platformCommissionPercent: policy.commissionPercent,
    stripeFeesCents: stripeProcessingFeeCents,
    percentToInstructor,
  };
}

/**
 * Calculates the financial breakdown for a Roll Review.
 *
 * Money flow:
 *   1. Instructor defines priceCents.
 *   2. Practitioner pays priceCents plus gross-up service fee.
 *   3. Stripe keeps stripeFeesCents from the total paid.
 *   4. sOSSiety keeps the full commission.
 *   5. Instructor receives priceCents minus commission after delivery.
 *
 * @param priceCents  Instructor-defined price in cents.
 * @param countryCode Instructor country ('US', 'BR', ...).
 */
export function calculatePriceBreakdown(
  priceCents: number,
  countryCode: string = 'US'
): PriceBreakdown {
  return calculateFeeBreakdown({
    instructorPriceCents: priceCents,
    countryCode,
    policy: ROLL_REVIEW_PRICING_POLICY,
  });
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function formatCurrency(cents: number, currency: string = 'USD'): string {
  const amount = cents / 100;
  const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function centsToDecimal(cents: number): number {
  return cents / 100;
}

export function decimalToCents(decimal: number): number {
  return Math.round(decimal * 100);
}
